import { LocDef, LocDefs, LocMap } from '@cm/core/localization';
import { HubFeature } from '@ats/api/cm';

const columnHeadingDescriptionStart = 'Column heading for: ';
const pathStart = 'Catalogs|Fulfillment|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.CredentialFulfillment,
	portal: 'SP',
};

type LocNameStart = 'SPFulfillmentCatalog';
export type SPFulfillmentCatalogLocNames =
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}ButtonDescriptionPreview`
	| `${LocNameStart}ContentNone`
	| `${LocNameStart}ContentElectronic`
	| `${LocNameStart}ContentPhysical`
	| `${LocNameStart}ContentBoth`
	| `${LocNameStart}TitleCredentialBased`
	| `${LocNameStart}TitleGeneralFulfillment`
	| `${LocNameStart}LabelDeliveryFormat`
	| `${LocNameStart}LabelFilterByDeliveryFormat`
	| `${LocNameStart}LabelType`
	| `${LocNameStart}LabelFilterByType`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelNameAbbreviation`
	| `${LocNameStart}LabelDescription`
	| `${LocNameStart}LabelQueue`
	| `${LocNameStart}LabelElectronic`
	| `${LocNameStart}LabelExternalId`;

export const spFulfillmentCatalogLocs: LocDefs<SPFulfillmentCatalogLocNames> = {
	SPFulfillmentCatalogTitlePage: {
		defaultValue: 'Fulfillments',
		description: 'Page Title.',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Title.PageTitle',
		...sharedProperties,
	},
	SPFulfillmentCatalogButtonDescriptionPreview: {
		defaultValue: 'Preview',
		description: 'Preview button.',
		displayPath: `${pathStart}Preview`,
		displayType: 'Button',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Button.DescriptionPreview',
		...sharedProperties,
	},
	SPFulfillmentCatalogContentNone: {
		defaultValue: 'None',
		description: 'Text shown for the delivery format: None.',
		displayPath: `${pathStart}None`,
		displayType: 'Content',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Content.None',
		...sharedProperties,
	},
	SPFulfillmentCatalogContentElectronic: {
		defaultValue: 'Electronic',
		description: 'Text shown for the delivery format: Electronic.',
		displayPath: `${pathStart}Electronic`,
		displayType: 'Content',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Content.Electronic',
		...sharedProperties,
	},
	SPFulfillmentCatalogContentPhysical: {
		defaultValue: 'Physical',
		description: 'Text shown for the delivery format: Physical.',
		displayPath: `${pathStart}Physical`,
		displayType: 'Content',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Content.Physical',
		...sharedProperties,
	},
	SPFulfillmentCatalogContentBoth: {
		defaultValue: 'Electronic + Physical',
		description: 'Text shown for the delivery format: Electronic + Physical.',
		displayPath: `${pathStart}Electronic + Physical`,
		displayType: 'Content',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Content.Both',
		...sharedProperties,
	},
	SPFulfillmentCatalogTitleCredentialBased: {
		defaultValue: 'Credential Based Fulfillment',
		description: 'Text shown for the type: Credential Based Fulfillment.',
		displayPath: `${pathStart}Credential Based Fulfillment`,
		displayType: 'Title',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Title.CredentialBased',
		...sharedProperties,
	},
	SPFulfillmentCatalogTitleGeneralFulfillment: {
		defaultValue: 'General Fulfillment',
		description: 'Text shown for the type: General Fulfillment.',
		displayPath: `${pathStart}General Fulfillment`,
		displayType: 'Title',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.Title.GeneralFulfillment',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelDeliveryFormat: {
		defaultValue: 'Delivery Format',
		description: `${columnHeadingDescriptionStart}Delivery Format`,
		displayPath: `${pathStart}Delivery Format`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.Format',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelFilterByDeliveryFormat: {
		defaultValue: 'Filter by Format',
		description: `Label for: Filter by Format`,
		displayPath: `${pathStart}Filter by Format`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.FieldLabel.Format',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelType: {
		defaultValue: 'Type',
		description: `${columnHeadingDescriptionStart}Type`,
		displayPath: `${pathStart}Type`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.Type',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelFilterByType: {
		defaultValue: 'Filter by Type',
		description: `Label for: Filter by Type`,
		displayPath: `${pathStart}Filter by Type`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.FieldLabel.Type',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelName: {
		defaultValue: 'Name',
		description: `${columnHeadingDescriptionStart}Name`,
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.Name',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelNameAbbreviation: {
		defaultValue: 'Name Abbr.',
		description: `${columnHeadingDescriptionStart}Name Abbr.`,
		displayPath: `${pathStart}Name Abbr.`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.NameAbbreviation',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelDescription: {
		defaultValue: 'Description',
		description: `${columnHeadingDescriptionStart}Description`,
		displayPath: `${pathStart}Description`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.Description',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelQueue: {
		defaultValue: 'Queue',
		description: `${columnHeadingDescriptionStart}Queue`,
		displayPath: `${pathStart}Queue`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.Queue',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelElectronic: {
		defaultValue: 'Electronic',
		description: `${columnHeadingDescriptionStart}Electronic`,
		displayPath: `${pathStart}Electronic`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.Electronic',
		...sharedProperties,
	},
	SPFulfillmentCatalogLabelExternalId: {
		defaultValue: 'External ID',
		description: `${columnHeadingDescriptionStart}External ID`,
		displayPath: `${pathStart}External ID`,
		displayType: 'Label',
		legacyName: 'Credential.Fulfillment/Catalogs.Fulfillments.ColumnHeading.ExternalID',
		...sharedProperties,
	},
};

export type SPFulfillmentCatalogLocType = LocMap<SPFulfillmentCatalogLocNames>;
